import { I18n } from '@calendly/i18n'
import { BareButton } from '@calendly/ui/components/bare-button'
import { Button } from '@calendly/ui/components/button'
import { ArrowRightIcon, EmailSendIcon } from '@calendly/ui/components/icons'
import classNames from 'classnames'
import { useRef, useState, useEffect, forwardRef, useImperativeHandle } from 'react'

import styles from './GrowableSearchSection.module.css'
import { IconEditWrite } from '../assets'
import { IconStop } from '../assets/icon-stop'
import { ChatSuggestions } from '../chatSuggestions'
import { CHAT_SUGGESTIONS } from '../constants'

// Configuration constants
const LINE_HEIGHT = 24 // Height of a single line in pixels
const MAX_LINES = 7 // Maximum number of lines before scrolling
const MAX_HEIGHT = LINE_HEIGHT * MAX_LINES // Calculate max height dynamically

export const GrowableSearchSection = forwardRef(({
  searchQuery,
  onSearchChange,
  onSearch,
  location,
  showSuggestions = false,
  onSuggestionClick = () => {},
  responseBeingGenerated = false,
  onCancelGeneration = () => {},
  includeWhiteFade = true,
  analyticsHandlers = {},
  onNewChatPressed,
  embedded = false,
}, ref) => {
  const textareaRef = useRef(null)
  const [shouldShowSuggestions, setShouldShowSuggestions] = useState(false)
  const [isAnimatingSuggestions, setIsAnimatingSuggestions] = useState(false)
  const [isActive, setIsActive] = useState(false)

  // Expose focus function to parent components
  useImperativeHandle(ref, () => ({
    focus: () => {
      if (textareaRef.current) {
        textareaRef.current.focus()
      }
    }
  }), [])

  // Handle fade-in animation when response generation stops
  useEffect(() => {
    if (showSuggestions && !responseBeingGenerated && !shouldShowSuggestions) {
      setIsAnimatingSuggestions(true)
      setShouldShowSuggestions(true)
    } else if (!showSuggestions || responseBeingGenerated) {
      setShouldShowSuggestions(false)
      setIsAnimatingSuggestions(false)
    }
  }, [showSuggestions, responseBeingGenerated, shouldShowSuggestions])

  const resetTextareaSize = () => {
    if (textareaRef.current) {
      const textarea = textareaRef.current
      textarea.style.height = `${LINE_HEIGHT}px`
      textarea.style.overflowY = 'hidden'
    }
  }

  const handleSearch = (e) => {
    e.stopPropagation() // Prevent event bubbling to container
    if (responseBeingGenerated) {
      onCancelGeneration()
    } else {
      onSearch(searchQuery)
      resetTextareaSize()
    }
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault() // Prevent default newline behavior
      if (responseBeingGenerated || !searchQuery || searchQuery.trim() === '') return
      handleSearch(e)
    }
    // If it's Shift+Enter, let the default behavior happen (new line)
  }

  const handleInputChange = (e) => {
    const textarea = e.target
    const value = e.target.value

    // Reset height to minHeight first
    textarea.style.height = `${LINE_HEIGHT}px`

    // Calculate content height without padding
    const computedStyle = window.getComputedStyle(textarea)
    const paddingTop = parseFloat(computedStyle.paddingTop)
    const paddingBottom = parseFloat(computedStyle.paddingBottom)
    const contentHeight = textarea.scrollHeight - paddingTop - paddingBottom

    if (contentHeight > LINE_HEIGHT) {
      if (contentHeight > MAX_HEIGHT) {
        textarea.style.height = `${MAX_HEIGHT}px`
        textarea.style.overflowY = 'auto'
      } else {
        textarea.style.height = `${contentHeight + paddingTop + paddingBottom}px`
        textarea.style.overflowY = 'hidden'
      }
    } else {
      textarea.style.overflowY = 'hidden'
    }

    onSearchChange(value)
  }

  const handleContainerClick = () => {
    // Focus the textarea when the container is clicked
    if (textareaRef.current) {
      textareaRef.current.focus()

      // Track textarea click
      if (analyticsHandlers.trackTextboxClick) {
        analyticsHandlers.trackTextboxClick(location)
      }
    }
  }

  const handleFocus = () => {
    setIsActive(true)
  }

  const handleBlur = () => {
    setIsActive(false)
  }

  const isSearchDisabled = !searchQuery || searchQuery.trim() === ''

  const Icon = embedded ? ArrowRightIcon : EmailSendIcon

  return (
    <div className={embedded ? styles.embeddedSearchWrapper : styles.searchWrapper}>
      {includeWhiteFade && <div className={styles.whiteFade} />}
      {shouldShowSuggestions && CHAT_SUGGESTIONS.length > 0 && (
        <div className={styles.chatSuggestionsContainer}>
          <div className={isAnimatingSuggestions ? styles.suggestionsFadeIn : styles.suggestionsVisible}>
            <ChatSuggestions
              chatSuggestions={CHAT_SUGGESTIONS}
              onSuggestionClick={onSuggestionClick}
              embedded={embedded}
            />
          </div>
        </div>
      )}
      {onNewChatPressed && !embedded && (
      <div className={styles.newChatButtonContainer}>
        <Button decoration="secondary-outline" onClick={onNewChatPressed} size="small">
          <div className={styles.iconContainer}>
            <IconEditWrite />
          </div>
          {I18n.t('publisher.agent_hub.new_chat')}
        </Button>
      </div>
      )}
      <div className={styles.backgroundWrapper}>
        <div
          className={`
            ${embedded ? styles.embeddedGrowableSearchContainer : styles.growableSearchContainer} 
            ${isActive ? styles.active : ''}
          `}
          onClick={handleContainerClick}
        >
          <div className={styles.textareaWrapper}>
            <textarea
              className={styles.growableTextarea}
              placeholder={I18n.t('publisher.agent_hub.composer.placeholder')}
              value={searchQuery}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              onFocus={handleFocus}
              onBlur={handleBlur}
              ref={textareaRef}
            />
          </div>
          <div className={styles.bottomRow}>
            <BareButton
              className={classNames(styles.sendButton, embedded && styles.sendButtonEmbedded)}
              onClick={handleSearch}
              disabled={isSearchDisabled && !responseBeingGenerated}
            >
              {responseBeingGenerated ? (
                <IconStop />
              ) : (
                <Icon />
              )}
            </BareButton>
          </div>
        </div>
      </div>
    </div>
  )
})
